import { type ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { type AuthState, AuthStateType } from '../../hooks/useAuth';

interface ManagementPageLayoutProps {
  authState: AuthState;
  canAccess: (authState: AuthState) => boolean;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  maxWidthClassName?: string;
  wrapContent?: boolean;
  redirectAuthenticatedTo?: string;
  redirectUnauthenticatedTo?: string;
}

export function ManagementPageLayout({
  authState,
  canAccess,
  children,
  className,
  contentClassName,
  maxWidthClassName = 'max-w-7xl',
  wrapContent = true,
  redirectAuthenticatedTo = '/dashboard',
  redirectUnauthenticatedTo = '/auth',
}: ManagementPageLayoutProps) {
  const navigate = useNavigate();
  const hasAccess = canAccess(authState);

  useEffect(() => {
    if (authState.type === AuthStateType.NoToken) {
      navigate(redirectUnauthenticatedTo);
      return;
    }

    if (authState.type === AuthStateType.Authenticated && !hasAccess) {
      navigate(redirectAuthenticatedTo);
    }
  }, [
    authState,
    hasAccess,
    navigate,
    redirectAuthenticatedTo,
    redirectUnauthenticatedTo,
  ]);

  if (!hasAccess) {
    return null;
  }

  return (
    <div className={className ?? 'flex-1 overflow-y-auto p-4 md:p-8'}>
      {wrapContent ? (
        <div className={contentClassName ?? `${maxWidthClassName} mx-auto`}>
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
}
